/* eslint-disable camelcase */
import React, { Component } from 'react';
import { graphql } from 'react-relay';
import PropTypes from 'prop-types';
import i18next from 'i18next';
import styled from 'react-emotion';
import changeCase from 'change-case';
import { Container } from 'semantic-ui-react';
import Icon from '~/modules/coreUI/components/basic/Icon';

import { Column, Row, LinearLayout } from '~/modules/coreUI/components/layouts/helpers/LinearLayout';
import { Label } from '~/modules/coreUI/components/basic/Labels';
import withRootQuery from '~/modules/core/utils/relayHelpers/withRootQuery';
import withMedia from '~/modules/core/utils/mediaHelpers/withMedia';
import Spacer from '~/modules/coreUI/components/layouts/helpers/Spacer';
import Separator from '~/modules/coreUI/components/layouts/helpers/Separator';
import Panel from '~/modules/ecommerceCoreUI/components/basic/Panel';
import urlFor from '~/modules/ecommerceCore/utils/UrlConcatination';
import externalRedirect from '~/modules/ecommerceCore/utils/externalRedirect';
import injectInBetween from '~/modules/core/utils/jsHelpers/injectElementBetweenChildElements';
import withUserInfo from '~/modules/core/utils/accessManagementHelpers/withUserInfo';

const ShippingWrapper = styled(Row)`
  flex-wrap: wrap;
`;

const LabelText = styled.pre`
  white-space: pre-wrap;
  line-height: 1.2;
  font-family: "Open Sans", sans-serif;
`;

const iconDictionary = {
  pickup: 'pickup',
  qpost: 'q-post',
  by_shop: 'by-shop',
  free_shipping: 'free-shipping',
};


class ShopInfo extends Component {
  renderMethod = (text, className) => (
    <Row spaceBetween={0.7} pb={0.5}>
      <Icon subtle className={`el-icon el-icon-${className}`} size="lg" title={text} />
      <Label secondary>
        <pre>{text}</pre>
      </Label>
    </Row>
  );

  render() {
    const { queryResult } = this.props;
    return (
      <Container>
        <Column leftAligned fullWidth spaceBetween={1}>
          <Spacer size={0.5} />
          <Label primary semiBold xxl>
            {i18next.t('eCommerceShops:ShopDetails.shopInfoTitle')}
          </Label>
          <Spacer size={0.5} />
          {queryResult?.shop?.about_vendor && (
            <React.Fragment>
              <Panel
                borderLessHeader
                borderLessBody
                headerTitle={i18next.t('eCommerceShops:ShopDetails.about')}
                body={(
                  <Label emphasized>
                    <LabelText>{queryResult.shop.about_vendor}</LabelText>
                  </Label>
                )}
              />
              <Separator vertical={false} separatorLength="full" />
            </React.Fragment>
          )}
          {queryResult?.shop?.policy && (
            <React.Fragment>
              <Panel
                borderLessHeader
                borderLessBody
                headerTitle={i18next.t('eCommerceShops:ShopDetails.policy')}
                body={(
                  <Label emphasized>
                    <LabelText>{queryResult.shop.policy}</LabelText>
                  </Label>
                )}
              />
              <Separator vertical={false} separatorLength="full" />
            </React.Fragment>
          )}
          <LinearLayout spaceBetween={1.5} fullWidth spaceBetweenJustified leftAligned row={this.props.media.minDesktop}>
            {queryResult.shop?.payment_methods.length > 0 && (
              <React.Fragment>
                <Panel
                  borderLessHeader
                  borderLessBody
                  headerTitle={i18next.t('eCommerceShops:ShopDetails.paymentMethods')}
                  body={(
                    <Row>
                      {queryResult?.shop?.payment_methods.map((method, index) => (
                        <React.Fragment key={method.code}>
                          {method.code === 'cash'
                            ? this.renderMethod(method.name, 'cash')
                            : this.renderMethod(method.name, 'online')}
                          {queryResult.shop.payment_methods.length - 1 !== index && <Separator vertical separatorLength="large" />}
                          <Spacer size={1} />
                        </React.Fragment>
                      ))}
                    </Row>
                  )}
                />
                {!this.props.media.minDesktop && <Separator vertical={false} separatorLength="full" />}
              </React.Fragment>
            )}
            {queryResult?.shop?.shipping_methods.length > 0 && (
              <Panel
                borderLessHeader
                borderLessBody
                headerTitle={i18next.t('eCommerceShops:ShopDetails.shippingMethods')}
                body={(
                  <React.Fragment>
                    <ShippingWrapper fullWidth leftAligned>
                      {injectInBetween(
                        queryResult?.shop?.grouped_shipping_methods.map(method => (
                          <Row rightAligned key={method.title}>
                            <Icon subtle className={`el-icon el-icon-${iconDictionary[method.code]}`} size="lg" title={method.description} />
                            <Spacer size={0.5} />
                            <Label secondary>
                              {changeCase.titleCase(method.title)}
                            </Label>
                          </Row>
                        )),
                        <Separator vertical separatorLength="large" />,
                        true,
                      )}
                    </ShippingWrapper>
                  </React.Fragment>
                )}
              />
            )}
          </LinearLayout>
          {queryResult.shop.active && this.props.authenticated && (
            <React.Fragment>
              <Separator vertical={false} separatorLength="full" />
              <Panel
                borderLessHeader
                borderLessBody
                headerTitle={i18next.t('eCommerceShops:ShopDetails.questionText')}
                headerActions={
                [{
                  label: i18next.t('eCommerceShops:ShopDetails.askShopLink'),
                  iconAfterName: 'el-icon el-icon-right',
                  onClicked: () => externalRedirect(urlFor('getAskShopLink', [queryResult.shop.name, queryResult.shop.ref_id])),
                }]
               }
                body={<span />}
              />
            </React.Fragment>
          )}
        </Column>
      </Container>
    );
  }
}


ShopInfo.propTypes = {
  queryResult: PropTypes.shape({}).isRequired,
  authenticated: PropTypes.bool.isRequired,
  media: PropTypes.shape({
    minDesktop: PropTypes.bool,
  }).isRequired,
};

export default withUserInfo(withMedia(withRootQuery(
  ShopInfo,
  graphql`
      query ShopInfoQuery($vendorID: ID!) {
        shop(id: $vendorID) {
          ref_id
          id
          name
          active
          policy
          about_vendor
          grouped_shipping_methods{
            code
            title
            description
          }
          shipping_methods {
            formatted_shipping_method {
              code
              title
              description
            }
            ref_id
          }
          payment_methods {
            name
            code
          }
        }
      }
    `,
  null,
  props => ({
    vendorID: props.vendorID,
  }),
)));
